import React from 'react';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    className?: string;
    maxVisiblePages?: number;
    itemsPerPage?: number;
    onItemsPerPageChange?: (itemsPerPage: number) => void;
    itemsPerPageOptions?: number[];
    showItemsPerPage?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    className = '',
    maxVisiblePages = 5,
    itemsPerPage,
    onItemsPerPageChange,
    itemsPerPageOptions = [5, 10, 20, 50],
    showItemsPerPage = false
}) => {
    if (totalPages <= 1 && !showItemsPerPage) return null;

    // Déterminer quelles pages afficher
    const getVisiblePageNumbers = () => {
        // Si le nombre total de pages est inférieur ou égal au nombre maximum de pages visibles,
        // on affiche toutes les pages
        if (totalPages <= maxVisiblePages) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        // Nombre de pages à afficher de chaque côté de la page courante
        const sidesCount = Math.floor((maxVisiblePages - 3) / 2); // -3 pour la page courante et les deux extrémités
        
        // Pages toujours visibles
        const pages: (number | 'ellipsis-start' | 'ellipsis-end')[] = [];
        
        // Toujours afficher la première page
        pages.push(1);
        
        // Déterminer s'il faut afficher des pointillés au début
        if (currentPage > 2 + sidesCount) {
            pages.push('ellipsis-start');
        }
        
        // Pages autour de la page courante
        const startPage = Math.max(2, currentPage - sidesCount);
        const endPage = Math.min(totalPages - 1, currentPage + sidesCount);
        
        for (let i = startPage; i <= endPage; i++) {
            if (i !== 1 && i !== totalPages) { // Éviter les doublons avec première et dernière page
                pages.push(i);
            }
        }
        
        // Déterminer s'il faut afficher des pointillés à la fin
        if (currentPage < totalPages - 1 - sidesCount) {
            pages.push('ellipsis-end');
        }
        
        // Toujours afficher la dernière page
        if (totalPages > 1) {
            pages.push(totalPages);
        }
        
        return pages;
    };

    const visiblePages = getVisiblePageNumbers();

    return (
        <div className={`flex flex-col md:flex-row items-center justify-center gap-4 mt-4 mb-10 ${className}`}>
            {/* Items per page selector */}
            {showItemsPerPage && onItemsPerPageChange && (
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Afficher</span>
                    <select
                        value={itemsPerPage}
                        onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                        className="select select-sm select-bordered"
                    >
                        {itemsPerPageOptions.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                    <span className="text-sm text-gray-600">par page</span>
                </div>
            )}

            {/* Pagination controls */}
            {totalPages > 1 && (
            <div className="join">
            {/* Bouton précédent */}
            <button
                className="join-item btn btn-sm md:btn-md"
                disabled={currentPage === 1}
                onClick={() => onPageChange(currentPage - 1)}
                aria-label="Page précédente"
            >
                «
            </button>

            {/* Pages numérotées avec pointillés */}
            {visiblePages.map((page, index) => {
                if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                    return (
                        <button key={`ellipsis-${index}`} className="join-item btn btn-sm md:btn-md btn-disabled">
                            ...
                        </button>
                    );
                }
                
                return (
                    <button
                        key={`page-${page}`}
                        className={`join-item btn btn-sm md:btn-md ${
                            currentPage === page ? "btn-active bg-primary text-white" : ""
                        }`}
                        onClick={() => onPageChange(page)}
                        aria-label={`Page ${page}`}
                        aria-current={currentPage === page ? "page" : undefined}
                    >
                        {page}
                    </button>
                );
            })}

            {/* Bouton suivant */}
            <button
                className="join-item btn btn-sm md:btn-md"
                disabled={currentPage === totalPages}
                onClick={() => onPageChange(currentPage + 1)}
                aria-label="Page suivante"
            >
                »
            </button>
            </div>
            )}
        </div>
    );
};
